import React, { useState, useEffect } from 'react';
import { Calendar, DollarSign, MessageSquare, Plus, Clock, CheckCircle2, AlertCircle, Trash2 } from 'lucide-react';
import API_BASE_URL from '../../Config';
import Swal from 'sweetalert2';

export default function SocioCompromisos({ socioId, token, formatCurrency, formatDate }) {
  const [compromisos, setCompromisos] = useState([]);
  const [creditos, setCreditos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [nuevoCompromiso, setNuevoCompromiso] = useState({
    creditoId: '',
    fechaPromesa: '',
    montoPrometido: '',
    observacion: ''
  });

  useEffect(() => {
    fetchCompromisos();
    fetchCreditos();
  }, [socioId]);

  const fetchCompromisos = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/compromisos/socio/${socioId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setCompromisos(await res.json());
      }
    } catch (err) {
      console.error("Error fetching compromisos", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditos = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/creditos/socio/${socioId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Filtrar solo préstamos que tengan saldo capital pendiente o cuotas pendientes
        setCreditos(data || []);
      }
    } catch (err) {
      console.error("Error fetching creditos", err);
    }
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE_URL}/compromisos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          credito: { id: nuevoCompromiso.creditoId },
          fechaPromesa: nuevoCompromiso.fechaPromesa,
          montoPrometido: nuevoCompromiso.montoPrometido,
          observacion: nuevoCompromiso.observacion,
          estadoCompromiso: 1 // Pendiente
        })
      });

      if (res.ok) {
        Swal.fire('Guardado', 'Compromiso de pago registrado con éxito', 'success');
        setShowForm(false);
        setNuevoCompromiso({ creditoId: '', fechaPromesa: '', montoPrometido: '', observacion: '' });
        fetchCompromisos();
      } else {
        Swal.fire('Error', 'No se pudo guardar el compromiso', 'error');
      }
    } catch (err) {
      console.error("Error saving compromiso", err);
    }
  };

  const getEstadoLabel = (estado) => {
    switch(estado) {
      case 1: return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-bold uppercase">Pendiente</span>;
      case 2: return <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-bold uppercase">Cumplido</span>;
      case 3: return <span className="px-2 py-0.5 bg-rose-100 text-rose-700 rounded-full text-[10px] font-bold uppercase">Incumplido</span>;
      default: return null;
    }
  };

  if (loading) return <div className="p-10 text-center text-slate-400">Cargando compromisos...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold text-slate-800">Compromisos de Pago</h3>
          <p className="text-xs text-slate-500">Historial de promesas y seguimiento de recuperación</p>
        </div>
        {!showForm && (
          <button 
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-all text-xs font-bold uppercase"
          >
            <Plus className="w-4 h-4" /> Nuevo Compromiso
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 animate-premium-in">
          <form onSubmit={handleGuardar} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Préstamo Vinculado</label>
                <select 
                  required
                  className="w-full p-2.5 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm font-bold"
                  value={nuevoCompromiso.creditoId}
                  onChange={(e) => setNuevoCompromiso({...nuevoCompromiso, creditoId: e.target.value})}
                >
                  <option value="">Seleccione un préstamo...</option>
                  {creditos.map(c => (
                    <option key={c.id} value={c.id}>ID: {c.id} - {c.lineaCredito?.nombre || 'Préstamo'} ({formatCurrency(c.monto)})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Fecha de Compromiso</label>
                <input 
                  type="date" 
                  required
                  className="w-full p-2.5 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm font-bold"
                  value={nuevoCompromiso.fechaPromesa}
                  onChange={(e) => setNuevoCompromiso({...nuevoCompromiso, fechaPromesa: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Monto a Pagar (Gs.)</label>
                <input 
                  type="number" 
                  required
                  placeholder="Ej: 500000"
                  className="w-full p-2.5 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm font-bold"
                  value={nuevoCompromiso.montoPrometido}
                  onChange={(e) => setNuevoCompromiso({...nuevoCompromiso, montoPrometido: e.target.value})}
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Observaciones</label>
              <textarea 
                className="w-full p-3 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 h-20 resize-none text-sm font-medium"
                placeholder="Detalle del compromiso..."
                value={nuevoCompromiso.observacion}
                onChange={(e) => setNuevoCompromiso({...nuevoCompromiso, observacion: e.target.value})}
              />
            </div>
            <div className="flex justify-end gap-3">
              <button 
                type="button" 
                onClick={() => setShowForm(false)}
                className="px-6 py-2 text-slate-500 font-bold hover:bg-slate-100 rounded-xl transition-all text-xs uppercase"
              >
                Cancelar
              </button>
              <button 
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all text-xs uppercase"
              >
                Guardar Compromiso
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {compromisos.length === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
            <Clock className="w-12 h-12 text-slate-200 mx-auto mb-4" />
            <p className="text-slate-400 font-medium">No hay compromisos de pago registrados para este socio.</p>
          </div>
        ) : (
          compromisos.map((c) => (
            <div key={c.id} className="bg-white border border-slate-100 p-4 rounded-2xl shadow-sm hover:border-blue-100 transition-all flex items-center justify-between group">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400 group-hover:bg-blue-50 group-hover:text-blue-500 transition-colors">
                  <Calendar className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h4 className="font-bold text-slate-800">Pago para el {formatDate(c.fechaPromesa)}</h4>
                    {getEstadoLabel(c.estadoCompromiso)}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Monto prometido: <span className="font-bold text-slate-700">{formatCurrency(c.montoPrometido)}</span>
                  </p>
                  <p className="text-[11px] text-slate-400 italic mt-1 italic">"{c.observacion}"</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-black text-slate-300 uppercase">Registrado el</p>
                <p className="text-xs font-bold text-slate-500">{new Date(c.createdAt).toLocaleDateString()}</p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
